import {
  getTimeDifference,
  IconClock,
  IconLightning,
  pluralize,
  Tag,
  TagColor,
  TagRounding,
  TagSize,
  TimeUnit,
} from '@leland-dev/leland-ui-library';
import { type FC, type ReactNode, type SVGProps } from 'react';

import { BOOTCAMP_SEATS_LEFT_THRESHOLD } from '../../utils/constants';

import { useBootcampUrgencyTagGetCohortQuery } from './__generated-gql-types__/BootcampUrgencyTag.generated';

interface BootcampUrgencyTagProps {
  cohortUrn: string;
  fallback?: ReactNode;
  noTag?: boolean;
}

export const BootcampUrgencyTag: FC<BootcampUrgencyTagProps> = ({
  cohortUrn,
  fallback = null,
  noTag = false,
}) => {
  const { data } = useBootcampUrgencyTagGetCohortQuery({
    variables: {
      cohortUrn,
    },
  });

  const cohort = data?.bootcampCohort;

  const startAt = cohort?.startAt;
  const seatsLeft = cohort?.bootcamp.maximumAttendees
    ? cohort.bootcamp.maximumAttendees - cohort.totalAttendees
    : null;

  const daysUntilFirstSession = startAt
    ? Math.round(getTimeDifference(new Date(), new Date(startAt), TimeUnit.DAY))
    : null;

  return (
    <>
      {seatsLeft != null && seatsLeft < BOOTCAMP_SEATS_LEFT_THRESHOLD ? (
        <InnerTag
          text={
            seatsLeft === 0
              ? 'No seats left'
              : `Only ${pluralize(seatsLeft, 'seat')} left`
          }
          Icon={IconLightning}
          noTag={noTag}
        />
      ) : daysUntilFirstSession &&
        daysUntilFirstSession >= 1 &&
        daysUntilFirstSession <= 7 ? (
        <InnerTag
          text={`${pluralize(daysUntilFirstSession, 'day')} left to enroll`}
          Icon={IconClock}
          noTag={noTag}
        />
      ) : (
        fallback
      )}
    </>
  );
};

const InnerTag: FC<{
  text: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
  noTag?: boolean;
}> = ({ text, Icon, noTag }) => {
  if (noTag) {
    return (
      <div className="flex items-center gap-1 text-sm font-medium uppercase tracking-wider text-leland-orange">
        <Icon className="size-4" />
        {text}
      </div>
    );
  }

  return (
    <Tag
      text={text}
      size={TagSize.XS}
      rounding={TagRounding.SM}
      tagColor={TagColor.ORANGE}
      LeftIcon={Icon}
    />
  );
};
